/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCodingRoutes } from "../../hooks/useCodingRoutes";
import useBatchStore from "../../store/batchStore";
import useAssignmentStore from "../../store/assignmentStore";
import useStudentStore from "../../store/studentStore";
import axiosInstance from "../../services/axiosInstance";
import {
  Box, Typography, Chip, CircularProgress, Alert,
  Stack,
  Accordion, AccordionSummary, AccordionDetails,
  Drawer, Button, Tooltip, Snackbar,
} from "@mui/material";
import GlassCard from "../../components/Cards/GlassCard";
import ExpandMoreRoundedIcon      from "@mui/icons-material/ExpandMoreRounded";
import EmojiEventsRoundedIcon     from "@mui/icons-material/EmojiEventsRounded";
import TableChartRoundedIcon      from "@mui/icons-material/TableChartRounded";
import AssignmentRoundedIcon      from "@mui/icons-material/AssignmentRounded";
import GroupsRoundedIcon          from "@mui/icons-material/GroupsRounded";
import SyncRoundedIcon            from "@mui/icons-material/SyncRounded";
import TimelineRoundedIcon        from "@mui/icons-material/TimelineRounded";
import LightbulbRoundedIcon       from "@mui/icons-material/LightbulbRounded";
import ReportProblemRoundedIcon   from "@mui/icons-material/ReportProblemRounded";
import AppBreadcrumbs             from "../../components/AppBreadcrumbs";
import {
  surfaceGradientSx, gradientTextSx, primaryChipSx, PRIMARY, SECONDARY,
} from "../../utils/colors";
import SolutionPreviewPanel           from "../../components/CodingTracker/SolutionPreviewPanel";
import BatchAnalyticsDashboard        from "../../components/CodingTracker/BatchAnalyticsDashboard";
import LeetCodeLeaderboard            from "../../components/CodingTracker/LeetCodeLeaderboard";
import LeetCodeAnalyticsTable         from "../../components/CodingTracker/LeetCodeAnalyticsTable";
import LeetCodeSegmentationCards      from "../../components/CodingTracker/LeetCodeSegmentationCards";
import LeetCodeSummaryCards           from "../../components/CodingTracker/LeetCodeSummaryCards";
import LeetCodeAtRiskSection          from "../../components/CodingTracker/LeetCodeAtRiskSection";
import LeetCodeDropOffSection         from "../../components/CodingTracker/LeetCodeDropOffSection";
import LeetCodeBatchInsightsSection   from "../../components/CodingTracker/LeetCodeBatchInsightsSection";
import DrillDownPanel                 from "../../components/analytics/DrillDownPanel";
import StudentProgressHeatmap         from "../../components/analytics/StudentProgressHeatmap";
import LeetCodeStudentPanel           from "../../components/leetcode/LeetCodeStudentPanel";
import LeetCodeActivityTrendsModal    from "../../components/CodingTracker/LeetCodeActivityTrendsModal";

const COOLDOWN_MS = 10 * 60 * 1000; // 10 minutes

const BatchDetailsPage = () => {
  const { batchId }  = useParams();
  const navigate     = useNavigate();
  const cr = useCodingRoutes();

  // ── UI state ────────────────────────────────────────────────────────────────
  const [studentCount,           setStudentCount]           = useState(0);
  const [previewOpen,            setPreviewOpen]            = useState(false);
  const [previewContent]                                    = useState(null);
  const [showLeetcode,           setShowLeetcode]           = useState(false);
  const [selectedLeetcodeStudent, setSelectedLeetcodeStudent] = useState(null);
  const [lcExpanded,             setLcExpanded]             = useState(null);

  // ── Assignment-mode ─────────────────────────────────────────────────────────
  const [analyticsLoading,           setAnalyticsLoading]           = useState(false);
  const [leetcodeAnalytics,          setLeetcodeAnalytics]          = useState([]);
  const [leetcodeAnalyticsLoading,   setLeetcodeAnalyticsLoading]   = useState(false);
  const [assessmentAnalytics,        setAssessmentAnalytics]        = useState([]);
  const [assessmentAnalyticsLoading, setAssessmentAnalyticsLoading] = useState(false);
  const [drillDownData,    setDrillDownData]    = useState([]);
  const [drillDownTitle,   setDrillDownTitle]   = useState("");
  const [drillDownType,    setDrillDownType]    = useState("");
  const [drillDownLoading, setDrillDownLoading] = useState(false);

  // ── LeetCode-mode ───────────────────────────────────────────────────────────
  const [leaderboard,            setLeaderboard]            = useState(null);
  const [leaderboardLoading,     setLeaderboardLoading]     = useState(false);
  const [lcSyncAnalytics,        setLcSyncAnalytics]        = useState([]);
  const [lcSyncAnalyticsLoading, setLcSyncAnalyticsLoading] = useState(false);
  const [lcAnalyticsLoaded,      setLcAnalyticsLoaded]      = useState(false);
  const [syncLoading,            setSyncLoading]            = useState(false);
  const [syncResult,             setSyncResult]             = useState(null);
  const [snackOpen,              setSnackOpen]              = useState(false);
  const [trendsOpen,             setTrendsOpen]             = useState(false);
  const [insightsExpanded,       setInsightsExpanded]       = useState(true);
  const [atRiskExpanded,         setAtRiskExpanded]         = useState(true);

  const { currentBatch, fetchBatchById, loading, error, batches } = useBatchStore();
  const { assignments } = useAssignmentStore();
  const { fetchStudents } = useStudentStore();

  // ── Derived: cooldown ───────────────────────────────────────────────────────
  const lastSyncedAt = useMemo(() => {
    if (!lcSyncAnalytics.length) return null;
    const dates = lcSyncAnalytics
      .map((r) => r.lastSyncedAt ? new Date(r.lastSyncedAt).getTime() : 0)
      .filter(Boolean);
    return dates.length ? Math.max(...dates) : null;
  }, [lcSyncAnalytics]);

  const cooldownRemaining = useMemo(() => {
    if (!lastSyncedAt) return 0;
    const diff = COOLDOWN_MS - (Date.now() - lastSyncedAt);
    return diff > 0 ? diff : 0;
  }, [lastSyncedAt]);

  const syncDisabled = syncLoading || cooldownRemaining > 0;

  const syncTooltip = useMemo(() => {
    if (syncLoading) return 'Sync in progress…';
    if (cooldownRemaining > 0) {
      const mins = Math.ceil(cooldownRemaining / 60_000);
      return `Cooldown active — wait ${mins} more minute${mins !== 1 ? 's' : ''} before refreshing`;
    }
    return 'Fetch latest LeetCode stats for all students in this batch and update the table';
  }, [syncLoading, cooldownRemaining]);

  // ── Accordion handler ───────────────────────────────────────────────────────
  const handleLcAccordion = (panel) => (_e, isOpen) => {
    setLcExpanded(isOpen ? panel : null);
    if (panel === 'analytics' && !isOpen) setSelectedLeetcodeStudent(null);
  };

  // ── Drill-down ──────────────────────────────────────────────────────────────
  const handleDrillDown = async (url, title, type) => {
    setDrillDownTitle(title);
    setDrillDownType(type);
    setDrillDownData([]);
    setDrillDownLoading(true);
    try {
      const { data } = await axiosInstance.get(url);
      setDrillDownData(
        (data.students || []).map((s, i) => ({ ...s, id: s.studentId || String(i) }))
      );
    } catch {
      setDrillDownData([]);
    } finally {
      setDrillDownLoading(false);
    }
  };

  // ── Fetch: batch + students ─────────────────────────────────────────────────
  useEffect(() => {
    fetchBatchById(batchId);
    fetchStudents(batchId);
    const batch = batches.find((b) => b._id === batchId);
    if (batch) setStudentCount(batch.studentCount || 0);
  }, [batchId, fetchBatchById, fetchStudents]);

  // ── Fetch: assignment-mode analytics (/student-stats) ──────────────────────
  const fetchBatchStudentStats = React.useCallback(() => {
    if (!batchId) return;
    setLeetcodeAnalyticsLoading(true);
    axiosInstance
      .get(`/analytics/batch/${batchId}/student-stats`)
      .then(({ data }) => {
        const rows   = data?.students ?? [];
        const sorted = [...rows].sort((a, b) => (b.problemsSolved ?? 0) - (a.problemsSolved ?? 0));
        const ranked = sorted.map((s, i) => ({ ...s, id: s.studentId ?? i, rank: i + 1 }));
        setLeetcodeAnalytics(ranked);
      })
      .catch(() => setLeetcodeAnalytics([]))
      .finally(() => setLeetcodeAnalyticsLoading(false));
  }, [batchId]);

  useEffect(() => { fetchBatchStudentStats(); }, [fetchBatchStudentStats]);

  // ── Fetch: leaderboard ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!batchId) return;
    setLeaderboardLoading(true);
    axiosInstance
      .get(`/leetcode/leaderboard/batch/${batchId}`)
      .then(({ data }) => setLeaderboard(data))
      .catch(() => setLeaderboard(null))
      .finally(() => setLeaderboardLoading(false));
  }, [batchId]);

  // ── Fetch: LeetCode global stats (/batch/:id/leetcode-analytics) ────────────
  const fetchLcSyncAnalytics = React.useCallback(() => {
    if (!batchId) return;
    setLcSyncAnalyticsLoading(true);
    axiosInstance
      .get(`/batch/${batchId}/leetcode-analytics`)
      .then(({ data }) => {
        const rows   = data?.students ?? [];
        const sorted = [...rows].sort((a, b) => (b.leetcodeTotalSolved ?? 0) - (a.leetcodeTotalSolved ?? 0));
        const ranked = sorted.map((s, i) => ({
          ...s,
          id:        s.studentId,
          rank:      i + 1,
          profileId: s.studentId,
        }));
        setLcSyncAnalytics(ranked);
      })
      .catch(() => setLcSyncAnalytics([]))
      .finally(() => {
        setLcSyncAnalyticsLoading(false);
        setLcAnalyticsLoaded(true);
      });
  }, [batchId]);

  // Fetch LeetCode data only on first tab open; skip if already loaded
  useEffect(() => {
    if (showLeetcode && !lcAnalyticsLoaded && !lcSyncAnalyticsLoading) {
      fetchLcSyncAnalytics();
    }
  }, [showLeetcode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Manual sync handler ─────────────────────────────────────────────────────
  const handleLeetcodeSync = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const { data } = await axiosInstance.post(`/leetcode/sync/batch/${batchId}`);
      setSyncResult({
        succeeded:       data.studentsUpdated ?? data.updatedCount ?? 0,
        failed:          data.studentsFailed  ?? data.failed?.length ?? 0,
        failedUsernames: data.failedUsernames ?? (data.failed ?? []).map((f) => f.username),
      });
      setSnackOpen(true);
      fetchLcSyncAnalytics();
    } catch {
      setSyncResult({ succeeded: 0, failed: -1, failedUsernames: [] });
      setSnackOpen(true);
    } finally {
      setSyncLoading(false);
    }
  };

  // ── Guards ──────────────────────────────────────────────────────────────────
  if (loading)
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;
  if (error)
    return <Box p={4}><Alert severity="error">{error}</Alert></Box>;

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, maxWidth: 1200, mx: "auto", minHeight: "100vh" }}>
      <AppBreadcrumbs
        items={[
          { label: "Coding Tracker", path: cr.trackerHome },
          { label: currentBatch?.name || "Batch" },
        ]}
      />

      {/* ── Header Card ── */}
      <GlassCard
        hoverable={false}
        sx={{
          p: { xs: 1.5, md: 2 }, mb: 3, borderRadius: 4, ...surfaceGradientSx,
          display: "flex", alignItems: "flex-start",
          justifyContent: "space-between", flexWrap: "wrap", gap: 2,
        }}
      >
        <Box>
          <Chip label={currentBatch?.code} size="small" sx={{ mb: 1.5, ...primaryChipSx }} />
          <Typography variant="h5" fontWeight={800} sx={{ ...gradientTextSx, mb: 0.5 }}>
            {currentBatch?.name}
          </Typography>
          {currentBatch?.description && (
            <Typography variant="body2" color="text.secondary">{currentBatch.description}</Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          {/* Toggle LeetCode / Assignment mode */}
          <Box
            onClick={() => setShowLeetcode((v) => !v)}
            sx={{
              p: 2, borderRadius: 4, border: "1px solid",
              borderColor: showLeetcode ? PRIMARY : SECONDARY,
              bgcolor: showLeetcode ? `${PRIMARY}18` : `${SECONDARY}18`,
              display: "flex", alignItems: "center", cursor: "pointer",
              transition: "box-shadow 0.2s, border-color 0.2s, background 0.2s",
              "&:hover": { boxShadow: 3 },
            }}
          >
            <Typography variant="body2" fontWeight={800} sx={{ color: showLeetcode ? PRIMARY : SECONDARY }}>
              {showLeetcode ? "Assessment Analytics" : "Leetcode Analytics"}
            </Typography>
          </Box>

          {/* Assignments nav */}
          <Box
            onClick={() => navigate(cr.assignments(batchId))}
            sx={{
              p: 2, borderRadius: 4, border: "1px solid", borderColor: "divider",
              display: "flex", alignItems: "center", cursor: "pointer",
              transition: "box-shadow 0.2s", "&:hover": { boxShadow: 3 },
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <AssignmentRoundedIcon sx={{ color: PRIMARY, fontSize: 20 }} />
              <Typography variant="body1" fontWeight={700}>Assignments</Typography>
              <Chip label={assignments.length || currentBatch?.assignmentCount || 0} size="small" sx={primaryChipSx} />
            </Stack>
          </Box>

          {/* Students nav */}
          <Box
            onClick={() => navigate(cr.students(batchId))}
            sx={{
              p: 2.5, borderRadius: 4, border: "1px solid", borderColor: "divider",
              display: "flex", alignItems: "center", cursor: "pointer",
              transition: "box-shadow 0.2s", "&:hover": { boxShadow: 3 },
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <GroupsRoundedIcon sx={{ color: PRIMARY }} />
              <Typography variant="body1" fontWeight={700}>Students</Typography>
              <Chip label={studentCount} size="small" sx={primaryChipSx} />
            </Stack>
          </Box>
        </Box>
      </GlassCard>

      {/* ── Assignment mode ── */}
      {!showLeetcode && <BatchAnalyticsDashboard students={leetcodeAnalytics} />}

      {!showLeetcode && (drillDownData.length > 0 || drillDownLoading) && (
        <Box sx={{ mt: 3 }}>
          <DrillDownPanel
            title={drillDownTitle} data={drillDownData}
            type={drillDownType} loading={drillDownLoading}
            batchId={batchId}
            onClose={() => { setDrillDownData([]); setDrillDownTitle(""); setDrillDownType(""); }}
          />
        </Box>
      )}

      {!showLeetcode && (
        <Box sx={{ mt: 3 }}><StudentProgressHeatmap batchId={batchId} /></Box>
      )}

      {/* ── LeetCode mode ── */}
      {showLeetcode && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Sync bar */}
          <Box
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 1, px: 2, py: 1, borderRadius: 2,
              border: '1px solid', borderColor: 'divider',
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'grey.50',
            }}
          >
            <Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                LeetCode Global Stats
              </Typography>
              <Typography variant="caption" color="text.disabled" display="block">
                Data sourced from LeetCode profile sync ·{' '}
                {lastSyncedAt
                  ? `Last synced: ${new Date(lastSyncedAt).toLocaleString()}`
                  : 'Run a sync to populate data for new batches'}
                {cooldownRemaining > 0 && (
                  <Typography component="span" variant="caption" sx={{ color: 'warning.main', ml: 1 }}>
                    · Cooldown: {Math.ceil(cooldownRemaining / 60_000)}m remaining
                  </Typography>
                )}
              </Typography>
            </Box>
            <Tooltip title={syncTooltip} arrow>
              <span>
                <Button
                  variant="outlined" size="small"
                  startIcon={syncLoading ? <CircularProgress size={14} thickness={5} color="inherit" /> : <SyncRoundedIcon />}
                  onClick={handleLeetcodeSync}
                  disabled={syncDisabled}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                  {syncLoading ? 'Syncing…' : cooldownRemaining > 0
                    ? `Cooldown (${Math.ceil(cooldownRemaining / 60_000)}m)`
                    : 'Sync LeetCode Data'}
                </Button>
              </span>
            </Tooltip>
          </Box>

          <LeetCodeSummaryCards batchId={batchId} />

          <Accordion
            expanded={insightsExpanded}
            onChange={(_, isOpen) => setInsightsExpanded(isOpen)}
            disableGutters
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '16px !important', overflow: 'hidden', '&:before': { display: 'none' } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreRoundedIcon />}
              sx={{ px: 2.5, py: 0.5, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5 } }}
            >
              <LightbulbRoundedIcon sx={{ fontSize: 20, color: 'warning.main' }} />
              <Typography variant="subtitle1" fontWeight={700}>Automated Batch Insights</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <LeetCodeBatchInsightsSection batchId={batchId} />
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={atRiskExpanded}
            onChange={(_, isOpen) => setAtRiskExpanded(isOpen)}
            disableGutters
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '16px !important', overflow: 'hidden', '&:before': { display: 'none' } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreRoundedIcon />}
              sx={{ px: 2.5, py: 0.5, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5 } }}
            >
              <ReportProblemRoundedIcon sx={{ fontSize: 20, color: 'error.main' }} />
              <Typography variant="subtitle1" fontWeight={700}>At Risk Students</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <LeetCodeAtRiskSection batchId={batchId} />
            </AccordionDetails>
          </Accordion>

          <LeetCodeDropOffSection batchId={batchId} />

          <LeetCodeSegmentationCards batchId={batchId} />

          {/* Trends trigger (before leaderboard) */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<TimelineRoundedIcon />}
              onClick={() => setTrendsOpen(true)}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
            >
              View Trends
            </Button>
          </Box>

          {/* Leaderboard accordion */}
          <Accordion
            expanded={lcExpanded === 'leaderboard'}
            onChange={handleLcAccordion('leaderboard')}
            disableGutters elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '16px !important', overflow: 'hidden', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}
              sx={{ px: 2.5, py: 0.5, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5 } }}
            >
              <EmojiEventsRoundedIcon sx={{ fontSize: 20, color: 'warning.main' }} />
              <Typography variant="subtitle1" fontWeight={700}>Leaderboard</Typography>
             
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <LeetCodeLeaderboard leaderboard={leaderboard} loading={leaderboardLoading} />
            </AccordionDetails>
          </Accordion>

          {/* Analytics table accordion */}
          <Accordion
            expanded={lcExpanded === 'analytics'}
            onChange={handleLcAccordion('analytics')}
            disableGutters elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '16px !important', overflow: 'hidden', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}
              sx={{ px: 2.5, py: 0.5, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5 } }}
            >
              <TableChartRoundedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
              <Typography variant="subtitle1" fontWeight={700}>Analytics</Typography>
              <Chip
                label={lcSyncAnalyticsLoading
                  ? <CircularProgress size={14} thickness={5} color="inherit" />
                  : lcSyncAnalytics.length}
                size="small" sx={{ ml: 0.5, ...primaryChipSx }}
              />
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <LeetCodeAnalyticsTable
                rows={lcSyncAnalytics}
                loading={lcSyncAnalyticsLoading}
                batchId={batchId}
                onSync={handleLeetcodeSync}
                onRowClick={(row) =>
                  setSelectedLeetcodeStudent((prev) =>
                    prev?.profileId === row.profileId ? null : row
                  )
                }
              />
            </AccordionDetails>
          </Accordion>

        </Box>
      )}

      {/* Sync result snackbar */}
      <Snackbar
        open={snackOpen} autoHideDuration={5000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity={syncResult?.failed === -1 ? 'error' : syncResult?.failed > 0 ? 'warning' : 'success'}
          variant="filled" sx={{ width: '100%', borderRadius: 2 }}
        >
          {syncResult?.failed === -1
            ? 'Sync failed — check network or server logs.'
            : `Sync complete · ${syncResult?.succeeded ?? 0} updated` +
              (syncResult?.failed > 0
                ? ` · ${syncResult.failed} failed: ${syncResult.failedUsernames.join(', ')}`
                : '')}
        </Alert>
      </Snackbar>

      <LeetCodeActivityTrendsModal
        open={trendsOpen}
        onClose={() => setTrendsOpen(false)}
        batchId={batchId}
      />

      {/* LeetCode Student Detail Drawer */}
      <Drawer
        anchor="right"
        open={Boolean(selectedLeetcodeStudent)}
        onClose={() => setSelectedLeetcodeStudent(null)}
        PaperProps={{ sx: { width: '80vw', maxWidth: '80vw', p: 0, overflowX: 'hidden' } }}
      >
        {selectedLeetcodeStudent && (
          <LeetCodeStudentPanel
            student={selectedLeetcodeStudent}
            onClose={() => setSelectedLeetcodeStudent(null)}
          />
        )}
      </Drawer>

      <Box sx={{ position: "relative" }}>
        <SolutionPreviewPanel open={previewOpen} onClose={() => setPreviewOpen(false)} title="Solution Preview">
          {previewContent}
        </SolutionPreviewPanel>
      </Box>
    </Box>
  );
};

export default BatchDetailsPage;
